import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, X, Check, Briefcase } from 'lucide-react'
import api from '../lib/api'

interface Position { id: string; name: string; color: string; isActive: boolean }

const PRESET_COLORS = [
  '#3B82F6', '#60A5FA', '#93C5FD', '#1D4ED8',
  '#EF4444', '#FCA5A5', '#DC2626', '#F97316',
  '#10B981', '#34D399', '#84CC16', '#16A34A',
  '#F59E0B', '#FDE68A', '#FBBF24', '#D97706',
  '#8B5CF6', '#A78BFA', '#6366F1', '#7C3AED',
  '#EC4899', '#F9A8D4', '#DB2777', '#BE185D',
  '#14B8A6', '#5EEAD4', '#06B6D4', '#0891B2',
  '#9CA3AF', '#6B7280', '#374151', '#111827',
]

export default function Positions() {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState({ name: '', color: '#3B82F6' })
  const [editForm, setEditForm] = useState({ name: '', color: '' })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function load() {
    setLoading(true)
    const res = await api.get('/positions')
    setPositions(res.data)
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  async function create() {
    if (!form.name.trim()) return
    setSaving(true); setError('')
    try {
      const res = await api.post('/positions', form)
      setPositions(prev => [...prev, res.data])
      setForm({ name: '', color: '#3B82F6' })
      setModal(false)
    } catch (e: any) {
      setError(e.response?.data?.error || 'Erreur')
    } finally { setSaving(false) }
  }

  function startEdit(p: Position) {
    setEditingId(p.id)
    setEditForm({ name: p.name, color: p.color })
  }

  async function saveEdit(id: string) {
    setSaving(true)
    try {
      const res = await api.put(`/positions/${id}`, editForm)
      setPositions(prev => prev.map(p => p.id === id ? res.data : p))
      setEditingId(null)
    } finally { setSaving(false) }
  }

  async function deletePosition(id: string) {
    if (!confirm('Supprimer ce poste ?')) return
    try {
      await api.delete(`/positions/${id}`)
      setPositions(prev => prev.filter(p => p.id !== id))
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur')
    }
  }

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'

  return (
    <div className="p-6 max-w-2xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Briefcase size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-900">Postes</h1>
        </div>
        <button onClick={() => setModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
          <Plus size={16} /> Nouveau poste
        </button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-400 text-sm">Chargement...</div>
        ) : positions.length === 0 ? (
          <div className="py-12 text-center text-gray-400 text-sm">Aucun poste créé</div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {positions.map(p => (
              <li key={p.id} className="group flex items-center gap-4 px-5 py-3 hover:bg-gray-50 transition-colors">
                {editingId === p.id ? (
                  /* Mode édition inline */
                  <div className="flex items-center gap-3 flex-1">
                    <ColorDot color={editForm.color} />
                    <input
                      autoFocus
                      value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      className="flex-1 border border-blue-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyDown={e => { if (e.key === 'Enter') saveEdit(p.id); if (e.key === 'Escape') setEditingId(null) }}
                    />
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {PRESET_COLORS.map(c => (
                        <button key={c} onClick={() => setEditForm(f => ({ ...f, color: c }))}
                          className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${editForm.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                          style={{ backgroundColor: c }} />
                      ))}
                    </div>
                    <button onClick={() => saveEdit(p.id)} disabled={saving}
                      className="p-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors">
                      <Check size={15} />
                    </button>
                    <button onClick={() => setEditingId(null)}
                      className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors">
                      <X size={15} />
                    </button>
                  </div>
                ) : (
                  /* Mode affichage */
                  <>
                    <ColorDot color={p.color} />
                    <span className="flex-1 text-sm font-medium text-gray-900">{p.name}</span>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => startEdit(p)}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => deletePosition(p.id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <p className="text-xs text-gray-400 mt-3">{positions.length} poste{positions.length > 1 ? 's' : ''}</p>

      {/* Modal création */}
      {modal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="font-bold text-gray-900">Nouveau poste</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              {error && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom du poste</label>
                <input autoFocus type="text" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className={inputCls} placeholder="ex : Serveur, Cuisinier..."
                  onKeyDown={e => e.key === 'Enter' && create()} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Couleur</label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <ColorDot color={form.color} size="lg" />
                  <span className="text-sm text-gray-600">Aperçu : <strong>{form.name || 'Nom du poste'}</strong></span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Annuler</button>
              <button onClick={create} disabled={saving || !form.name.trim()}
                className="px-4 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-lg">
                {saving ? 'Création...' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function ColorDot({ color, size = 'md' }: { color: string; size?: 'md' | 'lg' }) {
  const cls = size === 'lg' ? 'w-6 h-6' : 'w-4 h-4'
  return <div className={`${cls} rounded-full flex-shrink-0`} style={{ backgroundColor: color }} />
}
